import { ApiError } from "../i18n/errorCodes";

/** Back-off between presigned/save attempts: four retries, about 40 s total. */
export const SAVE_RETRY_DELAYS_MS: readonly number[] = [1000, 3000, 9000, 27000];

export function isTransientSaveError(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  return err.status === 0 || err.status === 429 || err.status >= 500;
}

/** Resolves after `ms`, or as soon as `signal` aborts, whichever is first. */
function waitOrAborted(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise<void>((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

/** Retry an uploaded object's save while its reservation remains active. */
export async function saveWithRetry<T>(
  save: () => Promise<T>,
  { delays = SAVE_RETRY_DELAYS_MS, signal }: { delays?: readonly number[]; signal?: AbortSignal } = {},
): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await save();
    } catch (err) {
      if (attempt >= delays.length || !isTransientSaveError(err)) throw err;
      if (signal?.aborted) throw new Error("Upload cancelled");
      // Race the back-off against a cancel so a cancel during a long wait
      // (up to 27s) turns the row cancelled promptly instead of waiting it out.
      await waitOrAborted(delays[attempt], signal);
      if (signal?.aborted) throw new Error("Upload cancelled");
    }
  }
}
